import { Link, useLocation, useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Lock, Bookmark, RefreshCcw, Crown, Loader2, Heart, LogOut } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSavedDestinations } from '@/hooks/useSavedDestinations';
import { Seo } from '@/components/Seo';

const MyTrips = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, signOut } = useAuth();
  const { data: saved, isLoading: savedLoading } = useSavedDestinations();

  if (loading) {
    return (
      <Layout>
        <Seo title="My Trips | Swell Breaks" description="Your saved surf destinations and trips." path="/my-trips" noIndex />
        <div className="container mx-auto px-4 py-20 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <Seo title="My Trips | Swell Breaks" description="Your saved surf destinations and trips." path="/my-trips" noIndex />
        <section className="py-12 md:py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h1 className="font-serif text-3xl md:text-5xl font-semibold text-foreground mb-4">
                My Trips
              </h1>
              <p className="text-muted-foreground max-w-lg mx-auto">
                Save your favorite trip plans and come back to them anytime
              </p>
            </div>

            <Card className="max-w-lg mx-auto border-border/50">
              <CardHeader className="text-center pb-4">
                <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <Lock className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="font-serif text-2xl">Sign in to save trips</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-muted-foreground text-center">
                  Create an account to save your trip plans, regenerate recommendations, and access exclusive features.
                </p>

                <div className="space-y-4">
                  {[
                    { icon: Bookmark, text: 'Save unlimited trip plans' },
                    { icon: RefreshCcw, text: 'Regenerate recommendations anytime' },
                    { icon: Crown, text: 'Access premium destinations' },
                  ].map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <feature.icon className="h-4 w-4 text-primary" />
                      </div>
                      <span className="text-sm text-foreground">{feature.text}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col gap-3 pt-4">
                  <Button
                    variant="default"
                    className="w-full"
                    onClick={() => navigate('/auth', { state: { from: location.pathname } })}
                  >
                    Create Account
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate('/auth', { state: { from: location.pathname } })}
                  >
                    Sign In
                  </Button>
                </div>

                <p className="text-center text-sm text-muted-foreground">
                  Or{' '}
                  <Link to="/trip-planner" className="text-primary hover:underline">
                    start a new trip plan
                  </Link>
                </p>
              </CardContent>
            </Card>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <Seo title="My Trips | Swell Breaks" description="Your saved surf destinations and trips." path="/my-trips" noIndex />
      <section className="py-12 md:py-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <Link to="/my-swell-breaks" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                ← My Swell Breaks
              </Link>
              <h1 className="font-serif text-3xl md:text-5xl font-semibold text-foreground mb-2 mt-2">
                My Trips
              </h1>
              <p className="text-muted-foreground">
                Signed in as <span className="text-foreground">{user.email}</span>
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={signOut} className="gap-2 self-start md:self-auto">
              <LogOut className="h-4 w-4" /> Sign out
            </Button>
          </div>

          <h2 className="font-serif text-2xl text-foreground mb-4 flex items-center gap-2">
            <Heart className="h-5 w-5 text-primary" /> Saved destinations
          </h2>

          {savedLoading ? (
            <div className="py-10 flex justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : !saved || saved.length === 0 ? (
            <Card className="border-border/50 bg-card border-foreground/20">
              <CardContent className="py-10 text-center">
                <p className="text-muted-foreground mb-4">You haven't saved any destinations yet.</p>
                <Button onClick={() => navigate('/destinations')}>Browse destinations</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {saved.map((row) => {
                const d = row.destinations;
                if (!d) return null;
                return (
                  <Link
                    key={row.id}
                    to={`/destinations/${d.slug}`}
                    className="group relative overflow-hidden rounded-xl border border-border/40 bg-card/70 backdrop-blur-sm"
                  >
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={d.hero_image_url ?? ''}
                        alt={d.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <p className="text-xs text-muted-foreground mb-1">{d.countries?.name}</p>
                      <h3 className="font-serif text-lg text-foreground">{d.name}</h3>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default MyTrips;
